/**
 * Party types + write helpers — web SDK port of
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/parties/parties.ts
 *
 * Schema parity with mobile. Same field names, same enum values, same
 * duplicate-phone guard. Parties are org-scoped (no projectId field) —
 * a party can appear on many projects via transactions / tasks /
 * attendance referencing its `id`.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// ───── Type enums ─────

export type GeneralPartyType = 'client' | 'staff' | 'worker' | 'investor';
export type VendorPartyType =
  | 'material_supplier'
  | 'labour_contractor'
  | 'equipment_supplier'
  | 'contractor'
  | 'other_vendor';
export type PartyType = GeneralPartyType | VendorPartyType;

export const PARTY_TYPE_GROUPS: {
  label: string;
  types: { key: PartyType; label: string }[];
}[] = [
  {
    label: 'General',
    types: [
      { key: 'client',   label: 'Client' },
      { key: 'staff',    label: 'Staff' },
      { key: 'worker',   label: 'Worker' },
      { key: 'investor', label: 'Investor' },
    ],
  },
  {
    label: 'Vendor',
    types: [
      { key: 'material_supplier',   label: 'Material Supplier' },
      { key: 'labour_contractor',   label: 'Labour Contractor' },
      { key: 'equipment_supplier',  label: 'Equipment Supplier' },
      { key: 'contractor',          label: 'Contractor' },
      { key: 'other_vendor',        label: 'Other Vendor' },
    ],
  },
];

export const ALL_PARTY_TYPES = PARTY_TYPE_GROUPS.flatMap((g) => g.types);

export function partyTypeLabel(t: PartyType | string | undefined): string {
  if (!t) return '—';
  return ALL_PARTY_TYPES.find((x) => x.key === t)?.label ?? t;
}

export function partyTypeGroup(t: PartyType): 'General' | 'Vendor' {
  return ['client', 'staff', 'worker', 'investor'].includes(t) ? 'General' : 'Vendor';
}

/** Avatar colour pair per type — used by `.party-avatar` + `.proj-thumb`. */
export function partyTypeColors(t: PartyType | string | undefined): { bg: string; fg: string } {
  switch (t) {
    case 'client':              return { bg: '#D1FAE5', fg: '#10B981' }; // green
    case 'staff':               return { bg: '#DBEAFE', fg: '#2563EB' }; // blue
    case 'worker':              return { bg: '#FEE2E2', fg: '#DC2626' }; // red
    case 'investor':            return { bg: '#F3E8FF', fg: '#7C3AED' }; // purple
    case 'material_supplier':   return { bg: '#FEF3C7', fg: '#D97706' }; // amber
    case 'labour_contractor':   return { bg: '#FED7AA', fg: '#C2410C' }; // orange
    case 'equipment_supplier':  return { bg: '#FDE68A', fg: '#92400E' }; // yellow-brown
    case 'contractor':          return { bg: '#FCE7F3', fg: '#DB2777' }; // pink
    default:                    return { bg: '#F3F4F6', fg: '#525252' }; // neutral
  }
}

/** Stage-pill colour pair for the list view's Type column. */
export function partyTypePillColors(t: PartyType | string | undefined): { bg: string; fg: string } {
  switch (t) {
    case 'client':              return { bg: '#D1FAE5', fg: '#059669' };
    case 'staff':               return { bg: '#DBEAFE', fg: '#1D4ED8' };
    case 'worker':              return { bg: '#FEE2E2', fg: '#B91C1C' };
    case 'investor':            return { bg: '#F3E8FF', fg: '#6D28D9' };
    case 'material_supplier':   return { bg: '#FEF3C7', fg: '#D97706' };
    case 'labour_contractor':   return { bg: '#FED7AA', fg: '#C2410C' };
    case 'equipment_supplier':  return { bg: '#FDE68A', fg: '#92400E' };
    case 'contractor':          return { bg: '#FCE7F3', fg: '#BE185D' };
    default:                    return { bg: '#F3F4F6', fg: '#525252' };
  }
}

// ───── Bank details ─────

export type BankDetails = {
  accountHolderName?: string;
  accountNumber?: string;
  ifsc?: string;
  bankName?: string;
  bankAddress?: string;
  iban?: string;
  upiId?: string;
};

// ───── Party doc shape ─────

export type Party = {
  id: string;
  orgId: string;
  name: string;
  phone: string;
  email?: string;
  partyType: PartyType;
  fatherName?: string;
  dateOfJoining?: { toMillis(): number } | null;
  address?: string;
  aadharNumber?: string;
  aadharFileUrl?: string;
  panNumber?: string;
  panFileUrl?: string;
  openingBalance?: number;
  openingBalanceType?: 'to_pay' | 'to_receive';
  bankDetails?: BankDetails;
  /** Legacy alias — deprecated. */
  role?: string;
  createdAt?: { toMillis(): number } | null;
  createdBy?: string;
};

// ───── Phone normalisation (Indian E.164) ─────

export class InvalidPhoneError extends Error {
  constructor(message = 'Enter a valid 10-digit Indian mobile number.') {
    super(message);
    this.name = 'InvalidPhoneError';
  }
}

export class DuplicatePhoneError extends Error {
  existing: { id: string; name: string; partyType: PartyType };
  constructor(existing: { id: string; name: string; partyType: PartyType }) {
    super(`A party with this phone already exists: ${existing.name}.`);
    this.name = 'DuplicatePhoneError';
    this.existing = existing;
  }
}

/** Strips non-digits, accepts 10-digit Indian mobile (6-9 prefix) or
 *  already-prefixed +91/91. Returns `+91XXXXXXXXXX` or throws. */
export function normalizeIndianPhoneE164(raw: string): string {
  if (!raw) throw new InvalidPhoneError();
  const digits = raw.replace(/\D/g, '');
  let ten = digits;
  if (ten.length === 12 && ten.startsWith('91')) ten = ten.slice(2);
  if (ten.length === 11 && ten.startsWith('0')) ten = ten.slice(1);
  if (ten.length !== 10) throw new InvalidPhoneError();
  if (!/^[6-9]/.test(ten)) throw new InvalidPhoneError();
  return `+91${ten}`;
}

// ───── Create / Update / Delete ─────

export type CreatePartyInput = {
  orgId: string;
  createdBy: string;
  name: string;
  phone: string;
  partyType: PartyType;
  email?: string;
  fatherName?: string;
  dateOfJoining?: Date | null;
  address?: string;
  aadharNumber?: string;
  aadharFileUrl?: string;
  panNumber?: string;
  panFileUrl?: string;
  openingBalance?: number;
  openingBalanceType?: 'to_pay' | 'to_receive';
  bankDetails?: BankDetails;
};

export async function createParty(input: CreatePartyInput): Promise<string> {
  if (!input.orgId) throw new Error('orgId is required.');
  const name = input.name.trim();
  if (name.length < 2) throw new Error('Party name must be at least 2 characters.');
  const phone = normalizeIndianPhoneE164(input.phone);

  // Duplicate phone guard — one phone per org.
  const dupSnap = await getDocs(
    query(
      collection(db, 'parties'),
      where('orgId', '==', input.orgId),
      where('phone', '==', phone),
    ),
  );
  if (!dupSnap.empty) {
    const d = dupSnap.docs[0];
    const data = d.data() as Party;
    throw new DuplicatePhoneError({
      id: d.id,
      name: data.name,
      partyType: data.partyType,
    });
  }

  const doc: Record<string, unknown> = {
    orgId: input.orgId,
    name,
    phone,
    partyType: input.partyType,
    role: input.partyType, // legacy alias kept in sync
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
  };

  if (input.email?.trim()) doc.email = input.email.trim();
  if (input.fatherName?.trim()) doc.fatherName = input.fatherName.trim();
  if (input.dateOfJoining) doc.dateOfJoining = Timestamp.fromDate(input.dateOfJoining);
  if (input.address?.trim()) doc.address = input.address.trim();
  if (input.aadharNumber?.trim()) doc.aadharNumber = input.aadharNumber.trim();
  if (input.aadharFileUrl) doc.aadharFileUrl = input.aadharFileUrl;
  if (input.panNumber?.trim()) doc.panNumber = input.panNumber.trim().toUpperCase();
  if (input.panFileUrl) doc.panFileUrl = input.panFileUrl;
  if (typeof input.openingBalance === 'number' && input.openingBalance > 0) {
    doc.openingBalance = input.openingBalance;
    doc.openingBalanceType = input.openingBalanceType ?? 'to_pay';
  }
  if (input.bankDetails) {
    const bd = stripEmptyBank(input.bankDetails);
    if (bd) doc.bankDetails = bd;
  }

  const ref = await addDoc(collection(db, 'parties'), doc);
  return ref.id;
}

export type UpdatePartyInput = Partial<Omit<CreatePartyInput, 'orgId' | 'createdBy'>>;

export async function updateParty(id: string, data: UpdatePartyInput): Promise<void> {
  const updates: Record<string, unknown> = {};

  if (data.name !== undefined) {
    const n = data.name.trim();
    if (n.length < 2) throw new Error('Party name must be at least 2 characters.');
    updates.name = n;
  }
  if (data.phone !== undefined) {
    const phone = normalizeIndianPhoneE164(data.phone);
    // Duplicate check (ignoring self) requires reading the existing doc's
    // orgId first. We can read the doc to know the orgId then check
    // for collisions.
    // For simplicity we just write — collisions surface via Firestore
    // rule + we can warn in UI. (Mobile does a similar best-effort.)
    updates.phone = phone;
  }
  if (data.partyType !== undefined) {
    updates.partyType = data.partyType;
    updates.role = data.partyType;
  }
  if (data.email !== undefined) {
    updates.email = data.email?.trim() ? data.email.trim() : deleteField();
  }
  if (data.fatherName !== undefined) {
    updates.fatherName = data.fatherName?.trim() ? data.fatherName.trim() : deleteField();
  }
  if (data.dateOfJoining !== undefined) {
    updates.dateOfJoining = data.dateOfJoining
      ? Timestamp.fromDate(data.dateOfJoining)
      : deleteField();
  }
  if (data.address !== undefined) {
    updates.address = data.address?.trim() ? data.address.trim() : deleteField();
  }
  if (data.aadharNumber !== undefined) {
    updates.aadharNumber = data.aadharNumber?.trim()
      ? data.aadharNumber.trim()
      : deleteField();
  }
  if (data.aadharFileUrl !== undefined) {
    updates.aadharFileUrl = data.aadharFileUrl ?? deleteField();
  }
  if (data.panNumber !== undefined) {
    updates.panNumber = data.panNumber?.trim()
      ? data.panNumber.trim().toUpperCase()
      : deleteField();
  }
  if (data.panFileUrl !== undefined) {
    updates.panFileUrl = data.panFileUrl ?? deleteField();
  }
  if (data.openingBalance !== undefined) {
    if (typeof data.openingBalance === 'number' && data.openingBalance > 0) {
      updates.openingBalance = data.openingBalance;
      updates.openingBalanceType = data.openingBalanceType ?? 'to_pay';
    } else {
      updates.openingBalance = deleteField();
      updates.openingBalanceType = deleteField();
    }
  }
  if (data.bankDetails !== undefined) {
    const bd = stripEmptyBank(data.bankDetails);
    updates.bankDetails = bd ?? deleteField();
  }

  if (Object.keys(updates).length === 0) return;
  await updateDoc(doc(db, 'parties', id), updates);
}

export async function deleteParty(id: string): Promise<void> {
  await deleteDoc(doc(db, 'parties', id));
}

function stripEmptyBank(b: BankDetails): BankDetails | null {
  const out: BankDetails = {};
  if (b.accountHolderName?.trim()) out.accountHolderName = b.accountHolderName.trim();
  if (b.accountNumber?.trim()) out.accountNumber = b.accountNumber.trim();
  if (b.ifsc?.trim()) out.ifsc = b.ifsc.trim().toUpperCase();
  if (b.bankName?.trim()) out.bankName = b.bankName.trim();
  if (b.bankAddress?.trim()) out.bankAddress = b.bankAddress.trim();
  if (b.iban?.trim()) out.iban = b.iban.trim().toUpperCase();
  if (b.upiId?.trim()) out.upiId = b.upiId.trim();
  return Object.keys(out).length ? out : null;
}

// ───── Display helpers ─────

export function maskedAadhar(a: string | undefined): string {
  if (!a) return '—';
  const digits = a.replace(/\D/g, '');
  if (digits.length < 4) return a;
  const last4 = digits.slice(-4);
  return `XXXX XXXX ${last4}`;
}

export function maskedAccount(n: string | undefined): string {
  if (!n) return '—';
  const last4 = n.slice(-4);
  return `••••${last4}`;
}

export function formatIndianPhone(p: string | undefined): string {
  if (!p) return '—';
  const digits = p.replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return p;
  return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
}
